import { useState, useEffect, useRef, useCallback } from 'react';
import {
  Card, CardContent, Typography, Button, Box,
  LinearProgress, Alert, Chip, Paper
} from '@mui/material';
import MathMarkdown from '../components/MathMarkdown';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import SkipNextIcon from '@mui/icons-material/SkipNext';
import { useQuery } from '@tanstack/react-query';
import { useSession } from '../context/SessionContext';
import { assessmentApi } from '../utils/api';
import { Question, AnswerResult, FollowupQuestion, FollowupChoice } from '../types';
import MathInput from '../components/MathInput';
import FollowupDialog from '../components/FollowupDialog';

export default function AssessmentPage() {
  const { session, updateStatus } = useSession();
  const { data: questions = [] } = useQuery<Question[]>({
    queryKey: ['assessment', 'questions'],
    queryFn: () => assessmentApi.getQuestions(),
  });
  const [currentIndex, setCurrentIndex] = useState(0);
  const [answer, setAnswer] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [startTime, setStartTime] = useState<Date | null>(null);
  
  // Progress tracking (local state for real-time updates)
  const [_completedCount, setCompletedCount] = useState(0);
  const [skippedCount, setSkippedCount] = useState(0);
  const skippedCountRef = useRef(0);
  
  // Followup dialog state
  const [showFollowup, setShowFollowup] = useState(false);
  const [followupQuestion, setFollowupQuestion] = useState<FollowupQuestion | null>(null);
  const [followupChoices, setFollowupChoices] = useState<FollowupChoice[]>([]);
  
  // Result feedback state
  const [showResult, setShowResult] = useState(false);
  const [lastResult, setLastResult] = useState<AnswerResult | null>(null);

  const inputRef = useRef<HTMLInputElement>(null);

  // Keep ref in sync so handleAssessmentComplete stays stable
  useEffect(() => { skippedCountRef.current = skippedCount; }, [skippedCount]);

  useEffect(() => {
    setStartTime(new Date());
    setAnswer('');
    setLoading(false); // Reset loading state when new question is ready
    inputRef.current?.focus();
  }, [currentIndex]);

  const currentQuestion = questions[currentIndex];
  const progress = questions.length > 0 
    ? ((currentIndex + 1) / questions.length) * 100 
    : 0;

  const handleAssessmentComplete = useCallback(async () => {
    if (skippedCountRef.current > 0) {
      await updateStatus('skipped_review');
    } else {
      await updateStatus('feedback_teaser');
    }
  }, [updateStatus]);

  useEffect(() => {
    if (questions.length > 0 && session) {
      const sessionCompletedCount = session.completed_questions?.length || 0;
      const sessionSkippedCount = session.skipped_questions?.length || 0;

      // Initialize local counts from session
      setCompletedCount(sessionCompletedCount);
      setSkippedCount(sessionSkippedCount);

      // Find next unanswered question
      const answeredIds = new Set([
        ...(session.completed_questions || []),
        ...(session.skipped_questions || []),
      ]);

      const nextUnanswered = questions.findIndex(q => !answeredIds.has(q.question_id));
      if (nextUnanswered >= 0) {
        setCurrentIndex(nextUnanswered);
      } else if (sessionCompletedCount + sessionSkippedCount >= questions.length) {
        // All questions answered, move to skipped review or feedback
        void handleAssessmentComplete();
      }
    }
  }, [questions, session, handleAssessmentComplete]);

  const handleSubmit = async () => {
    if (!answer.trim() || !currentQuestion || !startTime || submitting || loading) return;

    setSubmitting(true);
    setError('');

    try {
      const result = await assessmentApi.submitAnswer(
        currentQuestion.question_id,
        answer.trim(),
        startTime.toISOString(),
        new Date().toISOString()
      );

      setLastResult(result);

      if (result.followup_required && result.followup_question) {
        setFollowupQuestion(result.followup_question);
        setFollowupChoices(result.followup_choices);
        setShowFollowup(true);
        // Increment completed count immediately (don't wait for followup)
        setCompletedCount(prev => prev + 1);
        // If this was a skipped question being answered, decrement skipped count
        if (session?.skipped_questions?.includes(currentQuestion.question_id)) {
          setSkippedCount(prev => Math.max(0, prev - 1));
        }
        setSubmitting(false);
      } else {
        // Increment completed count for immediate progress update
        setCompletedCount(prev => prev + 1);
        // If this was a skipped question being answered, decrement skipped count
        if (session?.skipped_questions?.includes(currentQuestion.question_id)) {
          setSkippedCount(prev => Math.max(0, prev - 1));
        }
        setShowResult(true);
        setLoading(true); // Keep button disabled during transition
        setTimeout(() => {
          setShowResult(false);
          setSubmitting(false);
          moveToNext();
        }, 1500);
      }
    } catch {
      setError('Failed to submit answer. Please try again.');
      setSubmitting(false);
    }
  };

  const handleFollowupSubmit = async (selectedFollowup: string[], followupFreeResponse: string) => {
    if (!currentQuestion || !followupQuestion) return;

    const followupType = followupQuestion.followup_type;

    setLoading(true); // Disable submit while transitioning
    await assessmentApi.submitFollowup(
      currentQuestion.question_id,
      followupType === 'FR' ? [] : selectedFollowup,
      followupType === 'FR' ? followupFreeResponse : undefined
    );
    setShowFollowup(false);
    setShowResult(true);
    setTimeout(() => {
      setShowResult(false);
      setLoading(false);
      moveToNext();
    }, 1500);
  };

  const handleSkip = async () => {
    if (!currentQuestion || submitting || loading) return;

    setSubmitting(true);
    setLoading(true);
    try {
      await assessmentApi.skipQuestion(currentQuestion.question_id);
      // Increment skipped count for immediate tracking
      setSkippedCount(prev => prev + 1);
      moveToNext();
    } catch {
      setError('Failed to skip question. Please try again.');
      setLoading(false);
    } finally {
      setSubmitting(false);
    }
  };

  const moveToNext = () => {
    if (currentIndex < questions.length - 1) {
      setCurrentIndex(prev => prev + 1);
    } else {
      handleAssessmentComplete();
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey && answer.trim() && !submitting && !loading) {
      e.preventDefault();
      handleSubmit();
    }
  };

  if (questions.length === 0) {
    return (
      <Card>
        <CardContent sx={{ p: 4, textAlign: 'center' }}>
          <Typography>Loading questions...</Typography>
        </CardContent>
      </Card>
    );
  }

  if (!currentQuestion) {
    return (
      <Card>
        <CardContent sx={{ p: 4, textAlign: 'center' }}>
          <Typography>Preparing next step...</Typography>
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Card>
        <CardContent sx={{ p: 4 }}>
          {/* Progress Section */}
          <Box sx={{ mb: 4 }}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
              <Typography variant="body2" color="text.secondary">
                Question {currentIndex + 1} of {questions.length}
              </Typography>
              <Chip
                label={`${Math.round(progress)}% Complete`}
                color="primary"
                size="small"
                aria-label={`Assessment ${Math.round(progress)} percent complete`}
              />
            </Box>
            <LinearProgress
              variant="determinate"
              value={progress}
              aria-label={`Progress: ${Math.round(progress)}%`}
            />
          </Box>

          {/* Question Section */}
          <Paper variant="outlined" sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" component="h1" fontWeight={800} gutterBottom>
              Question {currentQuestion.display_order}
            </Typography>
            <MathMarkdown sx={{ fontSize: '1.1rem', '& p': { mt: 0, mb: 1 } }}>
              {currentQuestion.question_text}
            </MathMarkdown>
            {currentQuestion.graph_uri && currentQuestion.graph_uri.trim() !== '' && (
              <Box sx={{ mt: 3, display: 'flex', justifyContent: 'center' }}>
                <Box
                  component="img"
                  src={currentQuestion.graph_uri?.startsWith('/api') 
                    ? `${import.meta.env.VITE_API_URL?.replace(/\/api$/, '')}${currentQuestion.graph_uri}` 
                    : currentQuestion.graph_uri}
                  alt={`Graph for question ${currentQuestion.display_order}. ${currentQuestion.notes}`}
                  sx={{
                    maxWidth: '100%',
                    height: 'auto',
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 1,
                    boxShadow: 1,
                  }}
                  aria-label={`Graph for question ${currentQuestion.display_order}. ${currentQuestion.notes}`}
                />
              </Box>
            )}
          </Paper>

          {/* Answer Input */}
          <MathInput
            value={answer}
            onChange={(value) => setAnswer(value)}
            disabled={submitting || loading}
            label="Your Answer"
            onKeyDown={handleKeyDown}
          />

          {error && (
            <Alert severity="error" sx={{ my: 2 }} role="alert">
              {error}
            </Alert>
          )}

          {showResult && lastResult && (
            <Alert
              severity="info"
              sx={{ my: 2 }}
              role="status"
              aria-live="polite"
            >
              Answer recorded. Moving to next question...
            </Alert>
          )}

          {/* Action Buttons */}
          <Box display="flex" justifyContent="space-between" sx={{ mt: 3 }}>
            <Button
              variant="outlined"
              onClick={handleSkip}
              disabled={submitting || loading}
              startIcon={<SkipNextIcon />}
              aria-label="Skip this question"
            >
              Skip
            </Button>
            <Button
              variant="contained"
              onClick={handleSubmit}
              disabled={!answer.trim() || submitting || loading}
              endIcon={<NavigateNextIcon />}
              aria-label="Submit answer"
            >
              {submitting ? 'Submitting...' : 'Submit Answer'}
            </Button>
          </Box>
        </CardContent>
      </Card>

      {/* Followup Dialog */}
      <FollowupDialog
        open={showFollowup}
        followupQuestion={followupQuestion}
        followupChoices={followupChoices}
        originalQuestionGraphUri={currentQuestion?.graph_uri}
        originalQuestionGraphAlt={`Graph for question ${currentQuestion?.display_order}. ${currentQuestion?.notes || ''}`.trim()}
        onSubmit={handleFollowupSubmit}
        onError={(err) => setError(err)}
      />
    </>
  );
}
